import React, { useContext, useState, useRef, useEffect } from "react";

// Context imports
import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";

// React Icons
import { PiArrowBendDownRightLight } from "react-icons/pi";
import { Trash } from "../../../components/icons/svg/outline";

// Utils for time formatting
import {
  convert24To12String,
  removeLeadingZeros,
  decimalToHoursMinutes,
  formatDate,
  zeroToDash,
} from "../../../utils/datetime";

// Utils for shift calculations
import {
  recalculateWorkerShiftHours,
  recalculateWorkerDayHours,
  calculateTotal,
} from "../../../utils/timekeeping/shiftCalculations";

// Hooks
import { useWorkerShiftManagement } from "../../../hooks/timekeeping";
import { useUiText } from "../../../hooks/useUiText";
import { UI_COPY } from "../../../config";

// Components
import DeleteShiftModal from "./DeleteShiftModal";

/**
 * Helper function to update a specific shift field and cause a component re-render
 * to also update the hours totals with the new values. This also re-calculates
 * the total hours for the shift, based on the new input.
 *
 * @param {Array<Object>} prevData Completed-shifts day rows.
 * @param {number} dayIndex Day index.
 * @param {number} shiftIndex Shift index within the day.
 * @param {string} field Field key to update.
 * @param {*} value Next field value.
 * @returns {Array<Object>} Next day-rows array with recalculated hours.
 */
function updateCompletedShiftField(
  prevData,
  dayIndex,
  shiftIndex,
  field,
  value
) {
  const newData = [...prevData];

  // Clone the day and its shifts
  const updatedDay = { ...newData[dayIndex] };
  const updatedShifts = [...updatedDay.shifts];

  // Clone the specific shift, apply update
  const updatedShift = {
    ...updatedShifts[shiftIndex],
    [field]: value,
  };

  // Replace shift and day
  updatedShifts[shiftIndex] = updatedShift;
  // Pass date from day object to recalculateWorkerShiftHours
  updatedDay.shifts = recalculateWorkerShiftHours(updatedShifts, updatedDay.date);
  newData[dayIndex] = recalculateWorkerDayHours(updatedDay);

  return newData;
}

/**
 * Displays a summary of the users shifts for the week before submitting thier timesheet
 * with the option to edit shifts.
 *
 * Reads and writes shift data to the DB via the standard API calls.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.isEditing Whether rows are editable (swipe-to-delete, inputs).
 * @param {Function} props.setIsEditing Editing-mode setter.
 * @returns {JSX.Element}
 */
const Shifts = ({ isEditing, setIsEditing }) => {
  const { completedShifts, setCompletedShifts } =
    useContext(TimekeepingContext);
  const { t, formatUiDate } = useUiText();

  // Shift management hook
  const { removeShiftFromDay } = useWorkerShiftManagement(setCompletedShifts);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);

  // Swipe state - track which shift is swiped open (format: "dayIndex-shiftIndex")
  const [swipedShift, setSwipedShift] = useState(null);
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);
  const swipeThreshold = 50; // Minimum distance to trigger swipe
  const swipeableRefs = useRef({}); // Store refs and cleanup functions for each swipeable row
  
  // Hover state - track which day cell is hovered (for desktop >md screens)
  const [hoveredDay, setHoveredDay] = useState(null); // format: "dayIndex-shiftIndex"

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      // Cleanup all event listeners
      Object.keys(swipeableRefs.current).forEach((key) => {
        if (key.endsWith('_cleanup') && typeof swipeableRefs.current[key] === 'function') {
          swipeableRefs.current[key]();
        }
      });
      swipeableRefs.current = {};
    };
  }, []);

  // Handle delete button click
  /**
   * Open the delete-confirm modal with a snapshot of the shift.
   *
   * @param {number} dayIndex Day index.
   * @param {number} shiftIndex Shift index.
   * @param {Object} shift Shift row.
   * @param {Object} dayData Day object (`date`).
   * @returns {void}
   */
  const handleDeleteClick = (dayIndex, shiftIndex, shift, dayData) => {
    setShiftToDelete({
      dayIndex,
      shiftIndex,
      date: formatDate(dayData.date, "MM/DD/YYYY"),
      jobNum: shift.jobNum,
      startTime: convert24To12String(removeLeadingZeros(shift.startTime)),
      finishTime: convert24To12String(removeLeadingZeros(shift.finishTime)),
    });
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  /**
   * Remove the pending shift from the day via `removeShiftFromDay`.
   *
   * @returns {void}
   */
  const handleDeleteConfirm = () => {
    if (shiftToDelete) {
      removeShiftFromDay(shiftToDelete.dayIndex, shiftToDelete.shiftIndex);
      setShiftToDelete(null);
    }
  };

  // Handle modal close
  /**
   * Close the delete modal and clear `shiftToDelete`.
   *
   * @returns {void}
   */
  const handleModalClose = () => {
    setShowDeleteModal(false);
    setShiftToDelete(null);
  };

  // Close swiped shift when clicking/tapping elsewhere
  /**
   * Close any open swipe when the click is on the grid container.
   *
   * @param {React.MouseEvent} e Click event.
   * @returns {void}
   */
  const handleContainerClick = (e) => {
    // If clicking on the grid container (not on a shift row), close any open swipes
    if (e.target === e.currentTarget || e.target.closest('.grid')) {
      if (swipedShift) {
        setSwipedShift(null);
      }
    }
  };

  // Swipe handlers using refs to attach non-passive listeners
  /**
   * Attach non-passive swipe listeners so left-swipe can `preventDefault` scroll.
   *
   * @param {HTMLElement|null} element Row element.
   * @param {number} dayIndex Day index.
   * @param {number} shiftIndex Shift index.
   * @returns {Function} Cleanup that removes the listeners.
   */
  const setupSwipeListeners = (element, dayIndex, shiftIndex) => {
    if (!element || !isEditing) return () => {}; // Return no-op cleanup

    const shiftKey = `${dayIndex}-${shiftIndex}`;

    /**
     * Record the swipe start coordinates.
     *
     * @param {TouchEvent} e Touch start event.
     * @returns {void}
     */
    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      swipeStartX.current = touch.clientX;
      swipeStartY.current = touch.clientY;
    };

    /**
     * Prevent vertical page scroll while swiping left.
     *
     * @param {TouchEvent} e Touch move event.
     * @returns {void}
     */
    const handleTouchMove = (e) => {
      if (swipeStartX.current === null) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - swipeStartX.current;
      const deltaY = touch.clientY - swipeStartY.current;

      // Only allow horizontal swipe (prevent vertical scrolling interference)
      if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX < 0) {
        // Swiping left - prevent page scroll
        e.preventDefault();
      }
    };

    /**
     * Open delete on a left swipe; close swipe on tap or right swipe.
     *
     * @param {TouchEvent} e Touch end event.
     * @returns {void}
     */
    const handleTouchEnd = (e) => {
      if (swipeStartX.current === null) return;
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - swipeStartX.current;
      const deltaY = touch.clientY - swipeStartY.current;

      // Check if it's a left swipe (negative deltaX) and horizontal enough
      if (Math.abs(deltaX) > swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY) && deltaX < 0) {
        // Swipe left detected - open delete button
        setSwipedShift(shiftKey);
      } else if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
        // Small movement - treat as tap
        // Use functional update to get latest state
        setSwipedShift((currentSwiped) => {
          if (currentSwiped === shiftKey) {
            // If this shift is already swiped, close it
            return null;
          } else {
            // Otherwise, close any other open swipes
            return null;
          }
        });
      } else if (deltaX > 0) {
        // Swiping right - close any open swipes
        setSwipedShift(null);
      }

      swipeStartX.current = null;
      swipeStartY.current = null;
    };

    // Add non-passive event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Return cleanup function
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  };


  // Handle delete button click from swipe
  /**
   * Close the swipe row and open the delete-confirm modal.
   *
   * @param {number} dayIndex Day index.
   * @param {number} shiftIndex Shift index.
   * @param {Object} shift Shift row.
   * @param {Object} dayData Day object (`date`).
   * @returns {void}
   */
  const handleSwipeDeleteClick = (dayIndex, shiftIndex, shift, dayData) => {
    setSwipedShift(null); // Close swipe
    handleDeleteClick(dayIndex, shiftIndex, shift, dayData);
  };

  // If no data is available, show loading or empty state
  if (!completedShifts) {
    return (
      <div className="w-full p-4 text-center text-gray-500">
        {t(UI_COPY.timesheet.loading)}
      </div>
    );
  }

  return (
    <div className="w-full" onClick={handleContainerClick}>
      <div
        className={`grid grid-cols-10 gap-0 w-full border-2 rounded-lg box-border overflow-hidden ${
          isEditing ? "border-gray-300" : "border-gray-300"
        }`}
      >
        {/* Title Row */}
        <div className="col-span-6 bg-gray-300 py-1 px-1 font-bold border-gray-400 border-b-2 flex items-center justify-center text-base">
          {t(UI_COPY.timesheet.workDetails)}
        </div>
        <div className="col-span-4 bg-gray-300 py-1 px-1 font-bold border-l-2 border-b-2 border-gray-400 flex items-center justify-center text-base">
          {t(UI_COPY.timesheet.hours)}
        </div>

        {/* Header Row */}
        <div className="bg-gray-200 py-1 px-1 font-bold border-b-2 border-gray-400 flex items-center justify-center text-sm">
          {t(UI_COPY.timesheet.day)}
        </div>
        <div className="bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          {t(UI_COPY.timesheet.job)}
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          {t(UI_COPY.timesheet.timeIn)}
        </div>
        <div className="col-span-2 bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          {t(UI_COPY.timesheet.timeOut)}
        </div>
        <div className="bg-gray-200 py-1 px-1 font-bold border-b-2 border-l-[2px] border-l-gray-400 border-gray-400 flex items-center justify-center text-sm">
          RT
        </div>
        <div className="bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          OT
        </div>
        <div className="bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          DT
        </div>
        <div className="bg-gray-200 py-1 px-1 font-bold border-b-2 border-l border-gray-400 flex items-center justify-center text-sm">
          {t(UI_COPY.timesheet.total)}
        </div>

        {/* Data Rows */}
        {completedShifts.map((dayData, dayIndex) => {
          const isEvenDay = dayIndex % 2 === 0;
          const bgColor = isEvenDay ? "bg-gray-100" : "bg-gray-200";

          return (
            <React.Fragment key={`day-${dayIndex}`}>
              {dayData.shifts.map((shift, shiftIndex) => {
                const shiftKey = `${dayIndex}-${shiftIndex}`;
                const isSwiped = swipedShift === shiftKey;
                
                return (
                  <React.Fragment key={shiftKey}>
                    {/* Wrapper div that spans all columns for swipe functionality */}
                    <div
                      className="col-span-10 relative"
                      style={{ overflow: 'hidden' }}
                    >
                      {/* Delete button - revealed on swipe (mobile) */}
                      {isEditing && (
                        <div
                          className="absolute right-0 top-0 h-12 w-20 bg-red-500 flex items-center justify-center z-10 md:hidden"
                          style={{
                            right: isSwiped ? '0' : '-80px',
                            transition: 'right 0.3s ease-out',
                          }}
                        >
                          <button
                            className="w-full h-full flex items-center justify-center text-white"
                            onClick={() => handleSwipeDeleteClick(dayIndex, shiftIndex, shift, dayData)}
                          >
                            <Trash className="w-6 h-6" />
                          </button>
                        </div>
                      )}
                      
                      {/* Inner container with cells - transforms on swipe */}
                      <div
                        ref={(el) => {
                          const shiftKey = `${dayIndex}-${shiftIndex}`;
                          const cleanupKey = `${shiftKey}_cleanup`;
                          
                          if (el) {
                            // Cleanup old listeners if they exist
                            if (swipeableRefs.current[cleanupKey]) {
                              swipeableRefs.current[cleanupKey]();
                            }
                            
                            // Setup new listeners
                            swipeableRefs.current[shiftKey] = el;
                            if (isEditing) {
                              const cleanup = setupSwipeListeners(el, dayIndex, shiftIndex);
                              swipeableRefs.current[cleanupKey] = cleanup;
                            }
                          } else {
                            // Element unmounted - cleanup
                            if (swipeableRefs.current[cleanupKey]) {
                              swipeableRefs.current[cleanupKey]();
                              delete swipeableRefs.current[cleanupKey];
                            }
                            if (swipeableRefs.current[shiftKey]) {
                              delete swipeableRefs.current[shiftKey];
                            }
                          }
                        }}
                        className="grid grid-cols-10 gap-0"
                        style={{
                          transform: isSwiped ? 'translateX(-80px)' : 'translateX(0)',
                          transition: 'transform 0.3s ease-out',
                          touchAction: isEditing ? 'pan-x' : 'auto', // Allow horizontal panning, prevent vertical when editing
                        }}
                      >
                        <div
                          className={`group ${bgColor} py-3 px-1 border-b border-gray-300 flex items-center justify-center text-sm relative cursor-pointer h-12 ${isEditing ? 'hover:bg-gray-300 transition-colors' : ''}`}
                          onMouseEnter={() => {
                            if (isEditing) {
                              setHoveredDay(shiftKey);
                            }
                          }}
                          onMouseLeave={() => {
                            if (isEditing) {
                              setHoveredDay(null);
                            }
                          }}
                        >
                          {shiftIndex === 0 ? (
                            formatDate(dayData.date, "MM/DD")
                          ) : (
                            <PiArrowBendDownRightLight className="w-4 h-4 text-gray-500" />
                          )}
                          {/* Tooltip - show on all shifts of the day */}
                          <div className="absolute z-10 bg-black text-white px-2 py-1 rounded text-xs whitespace-nowrap -top-8 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                            {formatUiDate(dayData.date, "weekday")}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                          </div>
                          {/* Delete button - shown on hover for desktop (>md) */}
                          {isEditing && hoveredDay === shiftKey && (
                            <div className="absolute inset-0 flex items-center justify-center bg-red-50 opacity-0 md:opacity-100 hidden md:flex pointer-events-none rounded overflow-hidden">
                              <button
                                className="w-full h-full flex items-center justify-center bg-red-50 cursor-pointer pointer-events-auto hover:bg-red-100 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(dayIndex, shiftIndex, shift, dayData);
                                }}
                                title={t(UI_COPY.timesheet.deleteShift)}
                              >
                                <Trash className="w-5 h-5 text-red-700" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div
                          className={`group ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm relative cursor-pointer h-12`}
                        >
                          {shift.jobNum}
                          {/* Tooltip */}
                          <div className="absolute z-10 bg-black text-white px-2 py-1 rounded text-xs whitespace-nowrap -top-8 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                            {shift.JobDescription}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                          </div>
                        </div>
                        <div
                          className={`col-span-2 ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12 ${isEditing ? "bg-yellow-50" : ""}`}
                        >
                          {isEditing ? (
                            <input
                              type="time"
                              defaultValue={shift.startTime}
                              className="w-full h-8 text-xs text-center border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-300 rounded"
                              onChange={(e) => {
                                const startTime = e.target.value;
                                setCompletedShifts((prevData) =>
                                  updateCompletedShiftField(
                                    prevData,
                                    dayIndex,
                                    shiftIndex,
                                    "startTime",
                                    startTime
                                  )
                                );
                              }}
                            />
                          ) : (
                            convert24To12String(removeLeadingZeros(shift.startTime))
                          )}
                        </div>
                        <div
                          className={`col-span-2 ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12 ${isEditing ? "bg-yellow-50" : ""}`}
                        >
                          {isEditing ? (
                            <input
                              type="time"
                              defaultValue={shift.finishTime}
                              className="w-full h-8 text-xs text-center border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-300 rounded"
                              onChange={(e) => {
                                const finishTime = e.target.value;
                                setCompletedShifts((prevData) =>
                                  updateCompletedShiftField(
                                    prevData,
                                    dayIndex,
                                    shiftIndex,
                                    "finishTime",
                                    finishTime
                                  )
                                );
                              }}
                            />
                          ) : (
                            convert24To12String(removeLeadingZeros(shift.finishTime))
                          )}
                        </div>
                        <div
                          className={`${bgColor} py-3 px-1 border-b border-l-[2px] border-l-gray-400 flex items-center justify-center text-sm h-12`}
                        >
                          {zeroToDash(decimalToHoursMinutes(shift.regularTime, true))}
                        </div>
                        <div
                          className={`${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12`}
                        >
                          {zeroToDash(
                            decimalToHoursMinutes(shift.overtime || 0, true)
                          )}
                        </div>
                        <div
                          className={`${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12`}
                        >
                          {zeroToDash(
                            decimalToHoursMinutes(shift.doubleTime || 0, true)
                          )}
                        </div>
                        <div
                          className={`${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm font-medium h-12`}
                        >
                          {zeroToDash(
                            decimalToHoursMinutes(
                              (Number(shift.regularTime) || 0) +
                                (Number(shift.overtime) || 0) +
                                (Number(shift.doubleTime) || 0),
                              true
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              {/* Blank row separator between days (except after the last day) */}
              {dayIndex < completedShifts.length - 1 && (
                <React.Fragment>
                  <div className="h-4 md:h-1 bg-gray-300"></div>
                  <div className="h-4 md:h-1 bg-gray-300"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 col-span-2"></div>
                  <div className="h-4 md:h-1 bg-gray-300 border-l-2 border-l-gray-400"></div>
                  <div className="h-4 md:h-1 bg-gray-300"></div>
                  <div className="h-4 md:h-1 bg-gray-300"></div>
                  <div className="h-4 md:h-1 bg-gray-300"></div>
                </React.Fragment>
              )}
            </React.Fragment>
          );
        })}

        {/* Final Row with Edit Button and Totals */}
        <div className="col-span-2 bg-gray-300 border-t-2 border-r-2 border-gray-400"></div>
        <div
          className={`col-span-4 ease-in-out transition-colors border-t-2 rounded-b border-gray-400 border-t-gray-400 flex items-center justify-start h-12 ${
            isEditing ? "bg-yellow-200" : "bg-green-200"
          }`}
        >
          <button
            className="bg-transparent border-transparent font-bold px-4 rounded-b transition-colors duration-200 w-full focus:outline-none focus:ring-0 h-full"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing
              ? t(UI_COPY.timesheet.saveHours)
              : t(UI_COPY.timesheet.editHours)}
          </button>
        </div>
        <div className="bg-gray-200 h-12 py-3 px-1 border-t-2 border-l-[2px] border-l-gray-400 border-gray-400 flex items-center justify-center text-sm font-bold">
          {zeroToDash(
            decimalToHoursMinutes(
              calculateTotal(completedShifts, "regularHours"),
              true
            )
          )}
        </div>
        <div className="bg-gray-200 h-12 py-3 px-1 border-t-2 border-l border-gray-400 flex items-center justify-center text-sm font-bold">
          {zeroToDash(
            decimalToHoursMinutes(
              calculateTotal(completedShifts, "overtimeHours"),
              true
            )
          )}
        </div>
        <div className="bg-gray-200 h-12 py-3 px-1 border-t-2 border-l border-gray-400 flex items-center justify-center text-sm font-bold">
          {zeroToDash(
            decimalToHoursMinutes(
              calculateTotal(completedShifts, "doubleTimeHours"),
              true
            )
          )}
        </div>
        <div className="bg-gray-200 h-12 py-3 px-1 border-t-2 border-l border-gray-400 flex items-center justify-center text-sm font-bold">
          {zeroToDash(
            decimalToHoursMinutes(
              calculateTotal(completedShifts, "totalHours"),
              true
            )
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="w-full mt-2 text-center">
        <p className="text-xs italic text-gray-500">
          {isEditing ? (
            <>
              <span className="md:hidden">
                {t(UI_COPY.timesheet.swipeToDelete)}
              </span>
              <span className="hidden md:inline">
                {t(UI_COPY.timesheet.hoverToDelete)}
              </span>
            </>
          ) : (
            t(UI_COPY.timesheet.hoursProvisional)
          )}
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteShiftModal
        isOpen={showDeleteModal}
        onClose={handleModalClose}
        onConfirm={handleDeleteConfirm}
        shiftInfo={shiftToDelete}
      />
    </div>
  );
};

export default Shifts;
